import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "sonner";
import * as m from "@/paraglide/messages";
import {
  listFavorites,
  createFavorite,
  reorderFavorites,
  deleteFavorite,
  type FavoriteCreate,
  type FavoriteEntityType,
  type FavoriteResponse,
} from "@/lib/favorites-api";

const FAVORITES_KEY = "favorites";

/**
 * Hook to fetch user's favorites
 */
export function useFavorites(entityType?: FavoriteEntityType) {
  return useQuery({
    queryKey: [FAVORITES_KEY, {entityType}],
    queryFn: () => listFavorites(entityType),
  });
}

/**
 * Hook to create a favorite
 */
export function useCreateFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: FavoriteCreate) => createFavorite(request),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [FAVORITES_KEY]});
      toast.success(m.favorites_added());
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Hook to reorder favorites
 */
export function useReorderFavorites() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (favoriteIds: string[]) => reorderFavorites(favoriteIds),
    onMutate: async (newOrder) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({queryKey: [FAVORITES_KEY]});

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([FAVORITES_KEY, {entityType: undefined}]);

      // Optimistically update the cache
      queryClient.setQueryData([FAVORITES_KEY, {entityType: undefined}], (old: unknown) => {
        if (!old || typeof old !== "object" || !("favorites" in old)) return old;
        const oldData = old as {favorites: FavoriteResponse[]; total: number};

        // Reorder favorites based on newOrder
        const reordered = newOrder
          .map((id) => oldData.favorites.find((f) => f.id === id))
          .filter((f): f is FavoriteResponse => f !== undefined)
          .map((f, index) => ({...f, display_order: index}));

        return {
          ...oldData,
          favorites: reordered,
        };
      });

      return {previousData};
    },
    onError: (error: Error, _, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData([FAVORITES_KEY, {entityType: undefined}], context.previousData);
      }
      toast.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: [FAVORITES_KEY]});
    },
  });
}

/**
 * Hook to delete a favorite (remove from favorites)
 */
export function useDeleteFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (favoriteId: string) => deleteFavorite(favoriteId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [FAVORITES_KEY]});
      toast.success(m.favorites_removed());
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
